import { useState } from "react";
import {
  useFetchOrdersQuery,
  useTypedDispatch,
  useTypedSelector,
} from "../store";
import Pagination from "../components/common/Pagination";
import { FaExternalLinkAlt } from "react-icons/fa";
import TextField from "../components/common/TextField";
import useDebounce from "../hooks/useDebounce";
import { Link } from "react-router-dom";
import { RiEBike2Line } from "react-icons/ri";
import { MdOutlineShoppingBag } from "react-icons/md";
import { HiRefresh } from "react-icons/hi";
import { FeeTypeText, OrderStatus, OrderType } from "../types";
import RiderSelect from "../components/common/RiderSelect";
import OrderStatusSelect from "../components/common/OrderStatusSelect";
import DateField from "../components/common/DateField";
import Dropdown from "../components/common/Dropdown";
import ToggleSwitch from "../components/common/ToggleSwitch";
import Button from "../components/common/Button";
import { orderApi } from "../store/apis/order.api";
import { TagType } from "../configs";
import Skeleton from "../components/common/Skeleton";
import RushOrders from "../components/common/RushOrders";
import { tdClasses, thClasses, centeredTdClasses } from "../configs";
import { orderStatusOptions } from "../configs";

const Orders: React.FC = () => {
  const dispatch = useTypedDispatch();
  const city = useTypedSelector((state) => state.session.city);
  const restaurant = useTypedSelector((state) => state.session.restaurant);
  const [allCities, setAllCities] = useState<boolean>(false);
  const [rush, setRush] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [status, setStatus] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [displayId, setDisplayId] = useState<string>("");
  const debouncedDisplayId = useDebounce(displayId, 400);

  const {
    data: orders,
    refetch,
    isFetching,
  } = useFetchOrdersQuery(
    {
      page,
      perPage,
      displayId: debouncedDisplayId,
      restaurantId: restaurant?._id,
      status: status as OrderStatus,
      startDate,
      endDate,
      ...(allCities ? {} : { cityId: city?._id }),
    },
    {
      skip: !!debouncedDisplayId && debouncedDisplayId.length !== 6,
    }
  );

  const handleRefresh = () => {
    dispatch(
      orderApi.util.invalidateTags([{ type: TagType.Order, id: "LIST" }])
    );
    refetch();
  };

  return (
    <div className="w-full flex flex-col mx-auto p-4 gap-2">
      <div className="flex items-center space-x-2">
        <Dropdown
          id="orderStatus"
          label="Status :"
          placeholder="Sélectionnez un statut"
          options={orderStatusOptions}
          value={status}
          setFieldValue={(_, value) => setStatus(value)}
          className="border border-secondary"
          errors={{}}
          touched={{}}
        />
        <div className="w-1/5">
          <TextField
            id="name"
            value={displayId}
            onChange={(e) => setDisplayId((e.target as HTMLInputElement).value)}
            placeholder="Numéro de la commande"
          />
        </div>
        <div className="w-1/5">
          <DateField
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            onClear={() => setStartDate("")}
            label="Date de début"
            max={endDate || undefined}
          />
        </div>
        <div className="w-1/5">
          <DateField
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            onClear={() => setEndDate("")}
            label="Date de fin"
            min={startDate || undefined}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Button
          className="bg-primary p-2 text-white font-semibold w-40 justify-center rounded text-lg"
          onClick={handleRefresh}
        >
          <div className="flex items-center gap-2">
            <div> Actualiser </div> <HiRefresh className="w-6 h-6" />
          </div>
        </Button>
        <div className="flex items-center gap-2 text-lg font-semibold">
          <div> Tous les villes : </div>
          <ToggleSwitch
            isOn={allCities}
            onToggle={() => {
              setAllCities(!allCities);
            }}
          />
        </div>
        <div className="flex items-center gap-2 text-lg font-semibold">
          <div> Rush : </div>
          <ToggleSwitch
            isOn={rush}
            onToggle={() => {
              setRush(!rush);
            }}
          />
        </div>
      </div>
      {rush && (
        <div className="w-full my-12">
          <RushOrders />
        </div>
      )}
      <table
        className="table-fixed w-full rounded-xl"
        style={{ borderRadius: "50px" }}
      >
        <colgroup>
          <col style={{ width: "7%" }} />
          <col style={{ width: "4%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "6%" }} />
        </colgroup>
        <thead>
          <tr>
            <th key={0} className={thClasses}>
              Numéro
            </th>
            <th key={1} className={thClasses}>
              Type
            </th>
            <th key={2} className={thClasses}>
              Restaurant
            </th>
            <th key={3} className={thClasses}>
              Client
            </th>
            <th key={4} className={thClasses}>
              Livreur
            </th>
            <th key={5} className={thClasses}>
              Status
            </th>
            <th key={6} className={thClasses}>
              Total
            </th>
            <th key={7} className={thClasses}>
              Frais
            </th>
            <th key={8} className={thClasses}>
              Date de création
            </th>
            <th key={9} className={thClasses}>
              Mise à jour
            </th>
            <th key={10} className={thClasses}>
              Détails
            </th>
          </tr>
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan={11}>
                <Skeleton times={5} className="h-10" />
              </td>
            </tr>
          ) : (
            orders?.data?.map((row) => (
              <tr key={row._id}>
                <td className={centeredTdClasses}> #{row.displayId}</td>
                <td className={tdClasses}>
                  {row.orderType === OrderType.DELIVERY ? (
                    <RiEBike2Line className="w-6 h-6 text-primary mx-auto" />
                  ) : (
                    <MdOutlineShoppingBag className="w-6 h-6 text-primary mx-auto" />
                  )}
                </td>
                <td className={tdClasses}>{row.restaurant}</td>
                <td className={tdClasses}>{row.user}</td>
                <td className={tdClasses}>
                  <RiderSelect
                    orderId={row._id}
                    value={row.rider || null}
                    placeholder="Sélectionner livreur"
                  />
                </td>
                <td className={tdClasses}>
                  <OrderStatusSelect
                    orderType={row.orderType}
                    orderId={row._id}
                    value={row.status}
                  />
                </td>
                <td className={centeredTdClasses}>
                  {row.paymentInfo.totalPrice.text}
                </td>
                <td className={centeredTdClasses}>
                  {
                    <ul className="list-disc ml-4 text-sm text-gray-600">
                      {row.paymentInfo.fees.map((fee, index) => (
                        <li key={index}>
                          {`${FeeTypeText[fee.type]} : ${fee.value.text}`}{" "}
                        </li>
                      ))}
                    </ul>
                  }
                </td>
                <td className={tdClasses}>{row.createdAt}</td>
                <td className={tdClasses}>{row.updatedAt}</td>
                <td className={tdClasses}>
                  <Link className="w-full flex justify-center" to={row._id}>
                    <FaExternalLinkAlt className="w-8 text-primary" />
                  </Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: orders?.paginationInfo.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </div>
  );
};

export default Orders;
